import { format } from "date-fns";

import {
  MedicationAdministrationRead,
  MedicationAdministrationRequest,
} from "@/types/emr/medicationAdministration/medicationAdministration";
import {
  ACTIVE_MEDICATION_STATUSES,
  getMedicationActiveWindow,
  MedicationActiveWindow,
  MedicationRequestRead,
} from "@/types/emr/medicationRequest/medicationRequest";

// Types for grouped medications
export interface GroupedMedication {
  productId: string;
  productName: string;
  requests: MedicationRequestRead[];
  hasActiveRequests: boolean;
  hasPRN: boolean;
  routes: string[];
  lastAdministeredTime?: string;
}

// Constants
export const TIME_SLOTS = [
  { label: "12:00 AM - 06:00 AM", start: "00:00", end: "06:00" },
  { label: "06:00 AM - 12:00 PM", start: "06:00", end: "12:00" },
  { label: "12:00 PM - 06:00 PM", start: "12:00", end: "18:00" },
  { label: "06:00 PM - 12:00 AM", start: "18:00", end: "24:00" },
] as const;

export const STATUS_COLORS = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-yellow-50 text-yellow-700 border-yellow-200",
  default: "bg-red-50 text-red-700 border-red-200",
} as const;

// Utility Functions

export function getDosageFromInstruction(
  instruction: MedicationRequestRead["dosage_instruction"][number] | undefined,
) {
  return {
    site: instruction?.site,
    route: instruction?.route,
    method: instruction?.method,
    dose: instruction?.dose_and_rate?.dose_quantity && {
      value: instruction.dose_and_rate.dose_quantity.value,
      unit: instruction.dose_and_rate.dose_quantity.unit,
    },
  };
}
export function createMedicationAdministrationRequest(
  medication: MedicationRequestRead,
  encounterId: string,
): MedicationAdministrationRequest {
  return {
    request: medication.id,
    encounter: encounterId,
    ...(medication.medication?.code && {
      medication: {
        code: medication.medication?.code,
        display: medication.medication?.display,
        system: medication.medication?.system,
      },
    }),
    ...(medication.requested_product && {
      administered_product: medication.requested_product.id,
    }),
    occurrence_period_start: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    occurrence_period_end: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    note: "",
    status: "completed",
    // Default administration dosage from the first instruction
    dosage: (() => {
      const primaryInstruction = medication.dosage_instruction[0];
      return {
        site: primaryInstruction?.site,
        route: primaryInstruction?.route,
        method: primaryInstruction?.method,
        dose: primaryInstruction?.dose_and_rate?.dose_quantity && {
          value: primaryInstruction.dose_and_rate.dose_quantity.value,
          unit: primaryInstruction.dose_and_rate.dose_quantity.unit,
        },
      };
    })(),
  };
}

export function isTimeInSlot(
  date: Date,
  slot: { date: Date; start: string; end: string },
): boolean {
  const slotStartDate = new Date(slot.date);
  const [startHour] = slot.start.split(":").map(Number);
  const [endHour] = slot.end.split(":").map(Number);

  slotStartDate.setHours(startHour, 0, 0, 0);
  const slotEndDate = new Date(slotStartDate);
  slotEndDate.setHours(endHour, 0, 0, 0);

  return date >= slotStartDate && date < slotEndDate;
}

/**
 * Groups medications by their requested_product.id or medication.code
 * Returns sorted array with active groups first, then alphabetically by name
 */
export function groupMedicationsByProduct(
  medications: MedicationRequestRead[],
  administrations?: MedicationAdministrationRead[],
): GroupedMedication[] {
  const groupsMap = new Map<string, GroupedMedication>();

  medications.forEach((medication) => {
    // Use product ID if available, otherwise fall back to medication code
    const productId =
      medication.requested_product?.id ||
      medication.medication?.code ||
      medication.id;

    const productName =
      medication.requested_product?.name ||
      medication.medication?.display ||
      "Unknown Medication";

    if (!groupsMap.has(productId)) {
      groupsMap.set(productId, {
        productId,
        productName,
        requests: [],
        hasActiveRequests: false,
        hasPRN: false,
        routes: [],
      });
    }

    const group = groupsMap.get(productId)!;
    group.requests.push(medication);

    // Check if request is active
    const isActive = ACTIVE_MEDICATION_STATUSES.includes(
      medication.status as (typeof ACTIVE_MEDICATION_STATUSES)[number],
    );

    if (isActive) {
      group.hasActiveRequests = true;
    }

    // Check for PRN and collect unique routes across all instructions
    for (const di of medication.dosage_instruction) {
      if (di.as_needed_boolean) {
        group.hasPRN = true;
      }
      const route = di.route?.display;
      if (route && !group.routes.includes(route)) {
        group.routes.push(route);
      }
    }
  });

  // Calculate last administered time for each group
  if (administrations?.length) {
    groupsMap.forEach((group) => {
      const requestIds = new Set(group.requests.map((r) => r.id));
      const groupAdministrations = administrations.filter((a) =>
        requestIds.has(a.request),
      );

      if (groupAdministrations.length > 0) {
        const latestAdmin = groupAdministrations.reduce((latest, current) => {
          return new Date(current.occurrence_period_start) >
            new Date(latest.occurrence_period_start)
            ? current
            : latest;
        });
        group.lastAdministeredTime = latestAdmin.occurrence_period_start;
      }
    });
  }

  // Convert to array and sort: active groups first, then alphabetically
  return Array.from(groupsMap.values()).sort((a, b) => {
    if (a.hasActiveRequests && !b.hasActiveRequests) return -1;
    if (!a.hasActiveRequests && b.hasActiveRequests) return 1;
    return a.productName.localeCompare(b.productName);
  });
}

/**
 * Gets the latest active request from a group (most recently authored)
 */
export function getLatestActiveRequest(
  group: GroupedMedication,
): MedicationRequestRead | null {
  const activeRequests = group.requests.filter((r) =>
    ACTIVE_MEDICATION_STATUSES.includes(
      r.status as (typeof ACTIVE_MEDICATION_STATUSES)[number],
    ),
  );

  if (activeRequests.length === 0) return null;

  return activeRequests.reduce((latest, current) => {
    const latestDate = new Date(latest.authored_on || latest.created_date);
    const currentDate = new Date(current.authored_on || current.created_date);
    return currentDate > latestDate ? current : latest;
  });
}

/**
 * Gets administrations for all medications in a group within a time slot
 */
export function getGroupAdministrationsForTimeSlot(
  administrations: MedicationAdministrationRead[],
  group: GroupedMedication,
  slotDate: Date,
  start: string,
  end: string,
): MedicationAdministrationRead[] {
  const requestIds = new Set(group.requests.map((r) => r.id));

  return administrations.filter((admin) => {
    if (!requestIds.has(admin.request)) return false;

    const adminDate = new Date(admin.occurrence_period_start);
    return isTimeInSlot(adminDate, { date: slotDate, start, end });
  });
}

/**
 * Combined active window for a group — the union across its active requests
 * (earliest start, latest end). Any open-ended request makes the group
 * open-ended. Falls back to all requests if none are active.
 */
export function getGroupActiveWindow(
  group: GroupedMedication,
): MedicationActiveWindow {
  const active = group.requests.filter((r) =>
    ACTIVE_MEDICATION_STATUSES.includes(
      r.status as (typeof ACTIVE_MEDICATION_STATUSES)[number],
    ),
  );
  const requests = active.length ? active : group.requests;

  let start: Date | undefined;
  let end: Date | undefined;
  let openEnded = false;

  for (const request of requests) {
    const window = getMedicationActiveWindow(request);
    if (!start || window.start < start) start = window.start;
    if (window.end === undefined) openEnded = true;
    else if (!end || window.end > end) end = window.end;
  }

  return { start: start ?? new Date(), end: openEnded ? undefined : end };
}

/**
 * How a time slot relates to an active window — used to shade out-of-window
 * slots, mark the start/end caps, and gate the Administer button.
 */
export interface SlotWindowState {
  inWindow: boolean;
  isStartSlot: boolean;
  isEndSlot: boolean;
}

export function getSlotWindowState(
  slot: { date: Date; start: string; end: string },
  window: MedicationActiveWindow,
): SlotWindowState {
  const slotStart = new Date(slot.date);
  slotStart.setHours(Number(slot.start.split(":")[0]), 0, 0, 0);
  const slotEnd = new Date(slotStart);
  slotEnd.setHours(Number(slot.end.split(":")[0]), 0, 0, 0);

  const beforeStart = slotEnd <= window.start;
  const afterEnd = window.end ? slotStart >= window.end : false;

  return {
    inWindow: !beforeStart && !afterEnd,
    isStartSlot: window.start >= slotStart && window.start < slotEnd,
    isEndSlot: !!window.end && window.end > slotStart && window.end <= slotEnd,
  };
}

/**
 * A slot's state for a whole group. `inWindow` is the union across the group's
 * active requests — true when the slot falls in ANY request's own window — so
 * gaps between non-overlapping courses for the same product are correctly
 * out-of-window (a single merged envelope would wrongly fill the gap). The
 * start/end caps still come from the group's combined envelope.
 */
export function getGroupSlotWindowState(
  slot: { date: Date; start: string; end: string },
  group: GroupedMedication,
  groupWindow: MedicationActiveWindow,
): SlotWindowState {
  const active = group.requests.filter((r) =>
    ACTIVE_MEDICATION_STATUSES.includes(
      r.status as (typeof ACTIVE_MEDICATION_STATUSES)[number],
    ),
  );
  const requests = active.length ? active : group.requests;

  const inWindow = requests.some(
    (r) => getSlotWindowState(slot, getMedicationActiveWindow(r)).inWindow,
  );
  const envelope = getSlotWindowState(slot, groupWindow);
  return {
    inWindow,
    isStartSlot: envelope.isStartSlot,
    isEndSlot: envelope.isEndSlot,
  };
}
